import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { publicAPI } from "../services/api";
import {
	Room,
	RoomEvent,
	LocalParticipant,
	Track,
	createLocalVideoTrack,
	createLocalAudioTrack,
	VideoPresets,
	DisconnectReason,
} from "livekit-client";
import { useToast } from "../components/ui/toast";
import { Button } from "../components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
	Video,
	VideoOff,
	Mic,
	MicOff,
	Radio,
	Square,
	Wifi,
	WifiOff,
	Eye,
} from "lucide-react";
import { cn } from "../lib/utils";

interface TokenValidationData {
	livestream: {
		id: string;
		title: string;
		description?: string;
		type: "video" | "audio" | "video_audio";
		status: "created" | "live" | "ended";
		is_recording_enabled: boolean;
	};
	permissions: {
		can_publish?: boolean;
		can_view?: boolean;
		max_viewers?: number;
	};
	expires_at: string;
}

const PublicStreamPublisher: React.FC = () => {
	const { streamId } = useParams<{ streamId: string }>();
	const [searchParams] = useSearchParams();
	const token = searchParams.get('token');
	const autoConnect = searchParams.get('auto_connect') !== '0'; // Default to true (auto-connect) unless explicitly set to '0'
	const { addToast } = useToast();

	const [tokenData, setTokenData] = useState<TokenValidationData | null>(null);
	const [isValidatingToken, setIsValidatingToken] = useState(true);
	const [tokenError, setTokenError] = useState<string | null>(null);
	const [room, setRoom] = useState<Room | null>(null);
	const [isConnected, setIsConnected] = useState(false);
	const [isConnecting, setIsConnecting] = useState(false);
	const [isPublishing, setIsPublishing] = useState(false);
	const [cameraEnabled, setCameraEnabled] = useState(false);
	const [microphoneEnabled, setMicrophoneEnabled] = useState(false);
	const [viewerCount, setViewerCount] = useState(0);
	const [hasManuallyDisconnected, setHasManuallyDisconnected] = useState(false);
	const [streamEnded, setStreamEnded] = useState(false);

	const videoElementRef = useRef<HTMLVideoElement>(null);
	const localParticipantRef = useRef<LocalParticipant | null>(null);
	const previewVideoTrackRef = useRef<any>(null);
	const previewAudioTrackRef = useRef<any>(null);

	const validateToken = useCallback(async () => {
		try {
			setIsValidatingToken(true);

			const response = await publicAPI.validateToken(token!);
			const data = response.data;

			if (!data.success) {
				throw new Error(data.message);
			}

			if (!data.data.permissions.can_publish) {
				setTokenError("This token does not have publishing permissions.");
				setTokenData(null);
				setIsValidatingToken(false);
				return;
			}

			if (data.data.livestream.status === 'ended') {
				setTokenError("This livestream has ended.");
				setTokenData(null);
				setIsValidatingToken(false);
				return;
			}

			setTokenData(data.data);
		} catch (error) {
			console.error("Token validation error:", error);
			if (error && typeof error === 'object' && 'response' in error) {
				const axiosError = error as any;
				setTokenError(axiosError.response?.data?.message || "Invalid or expired token.");
			} else {
				setTokenError(error instanceof Error ? error.message : "Invalid or expired token.");
			}
			setTokenData(null);
		} finally {
			setIsValidatingToken(false);
		}
	}, [token]);

	useEffect(() => {
		if (streamId && token) {
			validateToken();
		} else {
			setTokenError("Valid token is required to access this stream.");
			setIsValidatingToken(false);
			return;
		}

		return () => {
			// Cleanup preview tracks
			if (previewVideoTrackRef.current) {
				previewVideoTrackRef.current.stop();
			}
			if (previewAudioTrackRef.current) {
				previewAudioTrackRef.current.stop();
			}
			if (room) {
				room.disconnect();
			}
		};
	}, [streamId, token, validateToken, room]);

	const updateViewerCount = useCallback((currentRoom: Room) => {
		const viewers = Array.from(currentRoom.remoteParticipants.values()).filter(
			p => p.identity.startsWith('anonymous_viewer_') || p.identity.startsWith('viewer_')
		);
		setViewerCount(viewers.length);
	}, []);

	const connectToStream = useCallback(async () => {
		if (!streamId || !tokenData) return;

		setIsConnecting(true);
		setHasManuallyDisconnected(false); // Reset manual disconnect flag when connecting

		try {
			// Generate LiveKit token for publishing using public token
			const tokenResponse = await publicAPI.generateLiveKitToken({
				token: token!,
				identity: `publisher_${Date.now()}`,
				is_publisher: true,
			});

			const tokenResult = tokenResponse.data;
			if (!tokenResult.success) {
				throw new Error(tokenResult.message);
			}

			// Create room instance
			const newRoom = new Room();
			setRoom(newRoom);

			// Set up event listeners
			newRoom.on(RoomEvent.Connected, () => {
				console.log("Connected to stream");
				setIsConnected(true);
				setIsConnecting(false);
				localParticipantRef.current = newRoom.localParticipant;
			});

			newRoom.on(RoomEvent.Disconnected, (reason?: DisconnectReason) => {
				console.log("Disconnected from stream:", reason);

				setIsConnected(false);
				setRoom(null);
				setIsPublishing(false);
				setCameraEnabled(false);
				setMicrophoneEnabled(false);

				if (reason !== DisconnectReason.CLIENT_INITIATED) {
					addToast({
						title: "Connection Lost",
						description: "You have been disconnected from the stream.",
						variant: "destructive",
					});
				}
			});

			newRoom.on(RoomEvent.ParticipantConnected, () => {
				// Update viewer count when new participants join
				updateViewerCount(newRoom);
			});

			newRoom.on(RoomEvent.ParticipantDisconnected, () => {
				// Update viewer count when participants leave
				updateViewerCount(newRoom);
			});

			// console.log(process.env.REACT_APP_LIVEKIT_WS_URL!, tokenResult.data.token);

			// Connect to the room using the room from token response
			await newRoom.connect(
				process.env.REACT_APP_LIVEKIT_WS_URL!,
				tokenResult.data.token,
			);

			// Update initial viewer count
			updateViewerCount(newRoom);

		} catch (error) {
			console.error("Failed to connect to stream:", error);
			addToast({
				title: "Connection Failed",
				description: "Failed to connect to the stream. Please try again.",
				variant: "destructive",
			});
			setIsConnecting(false);
		}
	}, [streamId, tokenData, token, addToast, updateViewerCount]);

	// Auto-connect effect
	useEffect(() => {
		if (autoConnect && tokenData && !isConnected && !isConnecting && !tokenError && !hasManuallyDisconnected && !streamEnded) {
			connectToStream();
		}
	}, [autoConnect, tokenData, isConnected, isConnecting, tokenError, hasManuallyDisconnected, streamEnded, connectToStream]);



	const toggleCamera = async () => {
		try {
			if (cameraEnabled) {
				// Turn off camera
				if (isPublishing && room && localParticipantRef.current) {
					// If publishing, disable through LiveKit
					await localParticipantRef.current.setCameraEnabled(false);
				} else if (previewVideoTrackRef.current) {
					// If previewing, stop the preview track
					previewVideoTrackRef.current.stop();
					previewVideoTrackRef.current = null;
				}
				setCameraEnabled(false);
			} else {
				// Turn on camera
				if (isPublishing && room && localParticipantRef.current) {
					// If publishing, enable through LiveKit
					await localParticipantRef.current.setCameraEnabled(true);
					const videoTrack = localParticipantRef.current.getTrackPublication(Track.Source.Camera)?.track;
					if (videoTrack && videoElementRef.current) {
						videoTrack.attach(videoElementRef.current);
					}
				} else {
					// If not publishing, create preview track
					const videoTrack = await createLocalVideoTrack({
						resolution: VideoPresets.h720.resolution,
					});
					previewVideoTrackRef.current = videoTrack;
					if (videoElementRef.current) {
						videoTrack.attach(videoElementRef.current);
					}
				}
				setCameraEnabled(true);
			}
		} catch (error) {
			console.error("Camera toggle error:", error);
			addToast({
				title: "Camera Error",
				description: "Failed to toggle camera.",
				variant: "destructive",
			});
		}
	};

	const toggleMicrophone = async () => {
		try {
			if (microphoneEnabled) {
				// Turn off microphone
				if (isPublishing && room && localParticipantRef.current) {
					// If publishing, disable through LiveKit
					await localParticipantRef.current.setMicrophoneEnabled(false);
				} else if (previewAudioTrackRef.current) {
					// If previewing, stop the preview track
					previewAudioTrackRef.current.stop();
					previewAudioTrackRef.current = null;
				}
				setMicrophoneEnabled(false);
			} else {
				// Turn on microphone
				if (isPublishing && room && localParticipantRef.current) {
					// If publishing, enable through LiveKit
					await localParticipantRef.current.setMicrophoneEnabled(true);
				} else {
					// If not publishing, create preview track (just for testing, not attached to video element)
					const audioTrack = await createLocalAudioTrack();
					previewAudioTrackRef.current = audioTrack;
				}
				setMicrophoneEnabled(true);
			}
		} catch (error) {
			console.error("Microphone toggle error:", error);
			addToast({
				title: "Microphone Error",
				description: "Failed to toggle microphone.",
				variant: "destructive",
			});
		}
	};

	const startPublishing = async () => {
		if (!room || !localParticipantRef.current || !tokenData) return;

		try {
			const streamType = tokenData.livestream.type;

			// Stop preview tracks if they exist
			if (previewVideoTrackRef.current) {
				previewVideoTrackRef.current.stop();
				previewVideoTrackRef.current = null;
			}
			if (previewAudioTrackRef.current) {
				previewAudioTrackRef.current.stop();
				previewAudioTrackRef.current = null;
			}

			// Reset states before publishing
			const wasCameraEnabled = cameraEnabled;
			const wasMicEnabled = microphoneEnabled;
			setCameraEnabled(false);
			setMicrophoneEnabled(false);

			setIsPublishing(true);

			// Call API to change status to 'live'
			if (streamId && token) {
				await publicAPI.startPublishing({
					token: token,
					livestream_id: streamId
				});
			}

			// Enable camera and/or microphone based on stream type and previous state
			if ((streamType === 'video' || streamType === 'video_audio') && wasCameraEnabled) {
				await localParticipantRef.current.setCameraEnabled(true);
				setCameraEnabled(true);
				const videoTrack = localParticipantRef.current.getTrackPublication(Track.Source.Camera)?.track;
				if (videoTrack && videoElementRef.current) {
					videoTrack.attach(videoElementRef.current);
				}
			}

			if ((streamType === 'audio' || streamType === 'video_audio') && wasMicEnabled) {
				await localParticipantRef.current.setMicrophoneEnabled(true);
				setMicrophoneEnabled(true);
			}

			addToast({
				title: "Publishing Started",
				description: tokenData.livestream.is_recording_enabled
					? "You are now live streaming and recording."
					: "You are now live streaming.",
				variant: "success",
			});
		} catch (error) {
			console.error("Start publishing error:", error);
			addToast({
				title: "Publishing Error",
				description: "Failed to start publishing.",
				variant: "destructive",
			});
			setIsPublishing(false);
		}
	};

	const stopPublishing = async () => {
		// Set manual disconnect flag to prevent auto-reconnect
		setHasManuallyDisconnected(true);
		// Mark stream as ended
		setStreamEnded(true);

		try {
			// Call the public endStream API to properly end the stream and stop recording
			if (streamId && token) {
				await publicAPI.endStream({
					token: token,
					livestream_id: streamId
				});
				console.log('Stream ended successfully via API');
			}
		} catch (error) {
			console.error('Failed to end stream via API:', error);
			// Continue with local cleanup even if API call fails
		}

		// Disconnect from LiveKit room
		if (room) {
			room.disconnect();
		}
		setIsPublishing(false);
	};

	if (isValidatingToken) {
		return (
			<div className="min-h-screen flex items-center justify-center bg-background">
				<div className="space-y-4 text-center">
					<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
					<p className="text-sm text-muted-foreground">Validating access token...</p>
				</div>
			</div>
		);
	}

	if (tokenError) {
		return (
			<div className="min-h-screen flex items-center justify-center bg-background">
				<Card className="w-full max-w-md">
					<CardHeader className="text-center">
						<CardTitle className="text-red-600">Access Denied</CardTitle>
						<CardDescription>{tokenError}</CardDescription>
					</CardHeader>
					<CardContent className="text-center">
						{/* <p className="text-sm text-muted-foreground mb-4">
							Please check your token and try again.
						</p> */}
						<Button onClick={() => window.location.reload()} variant="outline">
							Retry
						</Button>
					</CardContent>
				</Card>
			</div>
		);
	}

	if (streamEnded) {
		return (
			<div className="min-h-screen flex items-center justify-center bg-background">
				<Card className="w-full max-w-md">
					<CardHeader className="text-center">
						<CardTitle>Stream Ended</CardTitle>
						<CardDescription>
							The livestream has been ended successfully.
						</CardDescription>
					</CardHeader>
					<CardContent className="text-center">
						<div className="flex items-center justify-center mb-4">
							<Badge variant="secondary" className="text-base px-4 py-2">
								<Radio className="h-4 w-4 mr-2" />
								Offline
							</Badge>
						</div>
						<p className="text-sm text-muted-foreground">
							Thank you for streaming with us.
						</p>
					</CardContent>
				</Card>
			</div>
		);
	}

	if (!tokenData) {
		return null;
	}

	return (
		<div className="h-screen w-full bg-black relative overflow-hidden">
			{/* Video Element */}
			<video
				ref={videoElementRef}
				autoPlay
				muted
				playsInline
				className="w-full h-full object-cover"
			/>

			{/* No Video Placeholder */}
			{!cameraEnabled && tokenData && (
				<div className="absolute inset-0 flex items-center justify-center bg-black">
					<div className="text-center text-white">
						{tokenData.livestream.type === 'audio' ? (
							<>
								<Mic className="h-12 w-12 mx-auto mb-2 opacity-50" />
								<p>Audio Only Stream</p>
							</>
						) : (
							<>
								<VideoOff className="h-12 w-12 mx-auto mb-2 opacity-50" />
								<p>Camera Off</p>
							</>
						)}
					</div>
				</div>
			)}

			{/* Connection Status - Top Left */}
			<div className="absolute top-4 left-4">
				<div className="flex items-center gap-2 text-white">
					{isConnected ? (
						<Wifi className="h-4 w-4 text-green-500" />
					) : (
						<WifiOff className="h-4 w-4 text-red-500" />
					)}
					<span className="text-sm font-medium">
						{isConnected ? "Connected" : "Disconnected"}
					</span>
				</div>
			</div>

			{/* Viewer Count - Top Right */}
			<div className="absolute top-4 right-4">
				<div className="bg-black/60 backdrop-blur-sm rounded-full px-4 py-2 text-white flex items-center gap-2">
					<Eye className="h-4 w-4" />
					<span className="text-sm font-medium">{viewerCount}</span>
				</div>
			</div>

			{/* Bottom Controls */}
			<div className="absolute bottom-4 left-1/2 transform -translate-x-1/2">
				<div className="flex items-center gap-3">
					{!isConnected ? (
						<Button
							size="lg"
							onClick={connectToStream}
							disabled={isConnecting}
							className="bg-green-600 hover:bg-green-700 text-white px-8"
						>
							{isConnecting ? "Connecting..." : "Connect"}
						</Button>
					) : (
						<>
							{/* Main Stop/Start Publishing Button */}
							{!isPublishing ? (
								<Button
									size="lg"
									onClick={startPublishing}
									className="bg-red-600 hover:bg-red-700 text-white px-8"
								>
									<Radio className="h-4 w-4 mr-2" />
									Start Publishing
								</Button>
							) : (
								<Button
									size="lg"
									onClick={stopPublishing}
									className="bg-red-600 hover:bg-red-700 text-white px-8"
								>
									<Square className="h-4 w-4 mr-2" />
									Stop Publishing
								</Button>
							)}

							{/* Media Controls - Show when connected */}
							<div className="flex items-center gap-2 ml-4">
								{/* Camera Control - Only show for video or video_audio streams */}
								{tokenData && (tokenData.livestream.type === 'video' || tokenData.livestream.type === 'video_audio') && (
									<Button
										size="sm"
										variant="ghost"
										onClick={toggleCamera}
										disabled={!isConnected}
										className={cn(
											"w-10 h-10 rounded-full text-white hover:bg-white/20",
											cameraEnabled ? "bg-white/10" : "bg-red-600"
										)}
									>
										{cameraEnabled ? (
											<Video className="h-4 w-4" />
										) : (
											<VideoOff className="h-4 w-4" />
										)}
									</Button>
								)}

								{/* Microphone Control - Only show for audio or video_audio streams */}
								{tokenData && (tokenData.livestream.type === 'audio' || tokenData.livestream.type === 'video_audio') && (
									<Button
										size="sm"
										variant="ghost"
										onClick={toggleMicrophone}
										disabled={!isConnected}
										className={cn(
											"w-10 h-10 rounded-full text-white hover:bg-white/20",
											microphoneEnabled ? "bg-white/10" : "bg-red-600"
										)}
									>
										{microphoneEnabled ? (
											<Mic className="h-4 w-4" />
										) : (
											<MicOff className="h-4 w-4" />
										)}
									</Button>
								)}

							</div>
						</>
					)}
				</div>
			</div>
		</div>
	);
};

export default PublicStreamPublisher;
